import React, { JSX, useState } from 'react';
import { StockblocksStockItem, Folder } from 'stockblocks-client';

import useFilteredBins from '../../hooks/useFilteredBins';
import FolderAddButton from './FolderAddButton';
import FolderCard from './FolderCard';
import FolderSelectorInput from './FolderSelectorInput';
import FolderSelectorLoadingIndicator from './FolderSelectorLoadingIndicator';
import FolderShowAllButton from './FolderShowAllButton';

export type triggerEvent = React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>;

export type FolderSelectorProps = {
    title?: string;
    bins: Record<string, Folder>;
    isInSharedFoldersBeta?: boolean;
    stockItem?: StockblocksStockItem | null;
    folderClickedCallback: (arg: { event: triggerEvent; bin: Folder }) => void;
    createNewClickedCallback: (arg: { event: triggerEvent; name: string }) => void;
    isLoading: boolean;
};

const FolderSelector = ({
    title = 'Find or Create Folder',
    bins,
    isInSharedFoldersBeta = true,
    stockItem = null,
    folderClickedCallback,
    createNewClickedCallback,
    isLoading,
}: FolderSelectorProps): JSX.Element => {
    const [searchText, setSearchText] = useState('');
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const matchingBins = useFilteredBins(bins, searchText, isInSharedFoldersBeta, setInitialLoadComplete);

    const createNewFolder = (event: triggerEvent) => {
        createNewClickedCallback({ event, name: searchText });
        setSearchText('');
    };

    const selectFolder = (event: triggerEvent, bin: Folder) => {
        folderClickedCallback({ event, bin });
        setSearchText('');
    };

    return (
        <div
            id="member-bin-popover"
            data-testid="folder-selector-member-bin-popover"
            className="w-64 text-gray-950 bg-white rounded-xl text-sm"
            style={{ boxShadow: '0px 1px 4px 0px rgba(18, 18, 19, 0.20)' }}
        >
            <h3 className="px-4 py-3 m-0 text-sm leading-5 font-bold bg-gray-100 border-0 border-b border-gray-400 border-solid rounded-t-xl">
                {title}
            </h3>

            <div className={`p-4 ${isLoading ? 'h-80' : ''}`}>
                {isLoading ? (
                    <FolderSelectorLoadingIndicator />
                ) : (
                    <>
                        <FolderSelectorInput
                            searchText={searchText}
                            setSearchText={setSearchText}
                            isLoading={isLoading}
                        />
                        <div className="flex-nowrap h-60 flex flex-col overflow-y-scroll">
                            {initialLoadComplete && (
                                <FolderAddButton
                                    matchingBins={matchingBins}
                                    searchText={searchText}
                                    createNewFolder={createNewFolder}
                                />
                            )}

                            {matchingBins.map((bin) => {
                                const existsInBin = bin.contentIds.includes(stockItem?.contentId ?? -1);

                                return (
                                    <FolderCard
                                        bin={bin}
                                        existsInBin={existsInBin}
                                        handleBinClick={(e, bin) => selectFolder(e, bin)}
                                        key={bin.uniqueId}
                                        isShared={isInSharedFoldersBeta && bin.isShared}
                                    />
                                );
                            })}
                            <FolderShowAllButton />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default FolderSelector;
